"use client";

import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/components/ui/tabs";
import { Badge } from "@/app/components/ui/badge";
import { techCategories } from "@/app/lib/data";
import { motion, AnimatePresence } from "framer-motion";

const TechnologiesSection = () => {
  const [activeTab, setActiveTab] = useState(techCategories[0].name);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  return (
    <section
      id="technologies"
      className="py-20 md:py-28 relative overflow-hidden"
      style={{
        backgroundImage: "url('/technologies.jpg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      {/* Dark overlay for better content visibility */}
      <div className="absolute inset-0 bg-black/70 z-0"></div>

      {/* Subtle tech pattern overlay */}
      <div 
        className="absolute inset-0 z-0 opacity-20"
        style={{
          backgroundImage: "url('/images/tech-pattern.png')",
          backgroundSize: "200px",
          backgroundRepeat: "repeat",
        }}
      ></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl font-extrabold text-white mb-4 tracking-tight">
            Our <span className="bg-gradient-to-r from-cyan-400 to-white bg-clip-text text-transparent">Technology Stack</span>
          </h2>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            Cutting-edge tools to build high-performance and scalable digital solutions.
          </p>
        </motion.div>

        <div className="bg-gray-600 backdrop-blur-lg rounded-3xl shadow-xl overflow-hidden max-w-7xl mx-auto border border-gray-700">
  <div className="hidden md:block">
    <Tabs
      value={activeTab}
      onValueChange={handleTabChange}
      className="w-full"
    >
    <TabsList
  className="relative z-10 h-24 flex flex-wrap justify-center gap-3 bg-gray-600 backdrop-blur-md p-3 rounded-t-2xl border-b border-gray-600"
>
        {techCategories.map((category) => (
          <TabsTrigger
            key={category.name}
            value={category.name}
            className="flex flex-col items-center justify-center py-2 px-3 text-center data-[state=active]:bg-gray-600 data-[state=active]:shadow-xl data-[state=active]:text-white rounded-xl transition-all duration-300 hover:bg-gray-600/80"
            onMouseEnter={() => handleTabChange(category.name)}
          >
            <div className="mb-2 text-gray-200">
              {category.icon}
            </div>
            <span className="text-sm font-semibold tracking-tight text-gray-100">
              {category.name}
            </span>
          </TabsTrigger>
        ))}
      </TabsList>

      {techCategories.map((category) => (
        <TabsContent
          key={category.name}
          value={category.name}
          className="p-10 bg-gray-700/80 backdrop-blur-sm relative z-0"
        >
          <motion.div
            key={category.name}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.4 }}
            className=""
          >
            <div className="mb-6">
              <h3 className="text-3xl font-bold text-white mb-5 flex items-center">
                <span className="text-gray-300 mr-3 text-3xl">{category.icon}</span>
                {category.name}
              </h3>
              <p className="text-gray-300 text-base leading-relaxed">
                {category.description}
              </p>
            </div>

           <div className="flex flex-wrap gap-3 mt-4">
  {category.technologies.map((tech) => (
    <Badge
      key={tech.name}
      className={` px-4 py-2 text-sm font-medium rounded-full shadow-sm bg-gray-600 border border-gray-500 text-gray-100 transition-all duration-300 hover:text-white hover:shadow-white/50 hover:shadow-md`}
    >
      {tech.name}
    </Badge>
  ))}
</div>
          </motion.div>
        </TabsContent>
      ))}
    </Tabs>
  </div>

  {/* Mobile version */}
  <div className="md:hidden">
    <div className="grid grid-cols-2 gap-3 p-5 bg-gray-700 backdrop-blur-md rounded-t-2xl border-b border-gray-600">
      {techCategories.map((category) => (
        <button
          key={category.name}
          onClick={() => handleTabChange(category.name)}
          className={`flex flex-col items-center justify-center p-4 rounded-xl transition-all duration-200 text-sm font-semibold ${
            activeTab === category.name
              ? "bg-gray-600 shadow-lg text-white"
              : "bg-gray-600/70 hover:bg-gray-600 text-gray-200"
          }`}
        >
          <div className={`mb-2 text-xl ${
            activeTab === category.name ? "text-white" : "text-gray-300"
          }`}>{category.icon}</div>
          {category.name}
        </button>
      ))}
    </div>

    <div className="p-6 border-t border-gray-600 bg-gray-700/80 backdrop-blur-sm">
      {techCategories.map(
        (category) =>
          activeTab === category.name && (
            <AnimatePresence key={category.name}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                className="mt-5"
              >
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-white mb-5 flex items-center">
                    <span className="text-gray-300 mr-3 text-xl">{category.icon}</span>
                    {category.name}
                  </h3>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {category.description}
                  </p>
                </div>

                <div className="flex flex-wrap gap-2 mt-2">
                  {category.technologies.map((tech) => (
                    <Badge
                      key={tech.name}
                      className={`${tech.color} px-3 py-1 text-xs rounded-full bg-gray-600 border border-gray-500 text-gray-100`}
                    >
                      {tech.name}
                    </Badge>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          )
      )}
    </div>
  </div>
</div>
      </div>
    </section>
  );
};

export default TechnologiesSection;